/**
 * "Wait time between alerts" of one rule (tri-server #1488).
 *
 * The server stores `cooldownMinutes` (whole minutes, 1 to 10080 = 7 days) on the raw rule document;
 * empty means the global default (24 h). tri-model's `BusinessRule` class drops unknown fields, so
 * the value is carried beside the class: `withCooldownMinutes` attaches it to an instance (what
 * `JSON.stringify` then sends) and `cooldownMinutesOf` reads it back.
 */

export const MIN_COOLDOWN_MINUTES = 1;
/** 7 days; the server rejects anything above. */
export const MAX_COOLDOWN_MINUTES = 7 * 24 * 60;

export type CooldownUnit = 'minutes' | 'hours';

const isValidMinutes = (value: unknown): value is number =>
    typeof value === 'number'
    && Number.isInteger(value)
    && value >= MIN_COOLDOWN_MINUTES
    && value <= MAX_COOLDOWN_MINUTES;

/** The rule's stored wait time, or `null` for the default (absent, null or not a valid value). */
export const cooldownMinutesOf = (rule: any): number | null =>
    isValidMinutes(rule?.cooldownMinutes) ? rule.cooldownMinutes : null;

/** Attach the value to a model instance, or `null` to clear it on the server (`PUT` semantics). */
export const withCooldownMinutes = <T extends object>(rule: T, minutes: number | null): T =>
    Object.assign(rule, { cooldownMinutes: minutes });

/** The editor's two inputs for a stored value: whole hours show as hours, everything else as minutes. */
export const cooldownToFields = (minutes: number | null): { amount: string; unit: CooldownUnit } => {
    if (minutes === null) {
        return { amount: '', unit: 'minutes' };
    }

    return minutes % 60 === 0
        ? { amount: String(minutes / 60), unit: 'hours' }
        : { amount: String(minutes), unit: 'minutes' };
};

export type ParsedCooldown = { minutes: number | null; invalid: boolean };

/** Empty input is valid and means the default; anything but a whole number inside the bounds is invalid. */
export const parseCooldown = (amount: string, unit: CooldownUnit): ParsedCooldown => {
    const trimmed = amount.trim();

    if (trimmed === '') {
        return { minutes: null, invalid: false };
    }

    if (!/^\d+$/.test(trimmed)) {
        return { minutes: null, invalid: true };
    }

    const minutes = Number(trimmed) * (unit === 'hours' ? 60 : 1);

    return isValidMinutes(minutes) ? { minutes, invalid: false } : { minutes: null, invalid: true };
};
